import React from 'react'
import { dataTableActions } from 'isotope-client'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/styles'
import { PLANIFICATION_TABLE_NAME } from '../../utils/constants'
import { useGestionTacheContext } from '../../GestionTacheProvider'
import { getFilterValues } from '../../../planning/services/toolbar/toolbarSelector'
import SpringDataTableWithLoader from '../../../../../components/layout/SpringDataTableWithLoader'
import NoResultPage from '../../../../../components/NoResultPage'
import moment from 'moment'
import InfoTache from '../../../../common/information/InfoTache'
import FormattedTacheLieuMessage from '../common/FormattedTacheLieuMessage'
import TacheStatutIcon from '../common/TacheStatutIcon'
import { capitalize, formatDuration } from '../../../../../utils/utils'
import { formatFilters } from '../../utils/tacheUtils'
import { openTacheDetail } from '../../injectors/openTacheDetailsInjector'
import { compose } from 'redux'
import { injectTachePanel } from '../../form/tacheDetailPanel'
import { getTaskDetail } from '../../../scheduler/services/task.api'
import { Box } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import { showPrioriteWithRetard } from '../../utils/tacheUtilsTs'
import LockIcon from '@material-ui/icons/Lock'

const useStyles = makeStyles(theme => ({
	table: {
		borderCollapse: 'collapse',
		tableLayout: 'fixed',
		'& [class*="MuiTableCell-root"]': {
			height: 40,
			borderLeft: `1px solid ${theme.palette.border}`,
			borderRight: `1px solid ${theme.palette.border}`
		},
		'& [class*="MuiTableRow-head"]': {
			backgroundColor: theme.palette.primary.light
		},
		'& [class*="MuiTableCell-head"]': {
			padding: '0 5px',
			lineHeight: '1.3',
			'& span': {
				cursor: 'pointer'
			}
		},
		'& [class*="MuiTableCell-body"]:first-child': {
			padding: '0 5px',
			textAlign: 'center'
		},
		maxWidth: '100%'
	},
	leftContainer: {
		maxHeight: 'calc(100vh - 280px)',
		flex: '0 0 auto',
		display: 'block',
		maxWidth: '100%',
		overflow: 'auto'
	},
	lockBoxContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: 2
	},
	lockIcon: {
		height: 16,
		width: 16
	}
}))

const PlanificationTable = ({ filters, resetSelectedRows, openTacheDetail }) => {
	const { semaineSelection, isResultat } = useGestionTacheContext()
	const classes = useStyles()

	const filterValues = React.useMemo(() => ({
		...formatFilters(filters),
		debutSemaine: moment(semaineSelection).format('YYYY-MM-DD'),
		fromResultat: isResultat
	}), [filters, semaineSelection, isResultat])

	const headers = React.useMemo(() => {
		const result = [
			{
				key: 'code',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.tache" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '17%'
				},
				render: row => <span onClick={(event) => {
					getTaskDetail(row.id)
						.then((res) => openTacheDetail({
							...res,
							// Contournement pour afficher les infos de la solution sur l'onglet planification
							solutionTache: isResultat ? {
								start: row.dateDebutPlanification,
								end: row.dateFinPlanification,
								resources: row.affectations.map(a => ({ alias: a }))
							} : undefined
						}, isResultat))
					event.preventDefault()
					event.stopPropagation()
				}}><InfoTache tache={row} inTable /></span>
			},
			{
				key: 'priorite',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.priorite" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '3%',
					align: 'center'
				},
				render: row => showPrioriteWithRetard(row.priorite, row.dateInitiale, row.date)
			},
			{
				key: 'lieu',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.lieu" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '7%',
					align: 'center'
				},
				render: row => <FormattedTacheLieuMessage typeLieu={row.typeLieu} lieu={row.lieu} />
			}
		]

		if (!isResultat) {
			result.push({
				key: 'statut',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.statut" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '9%',
					align: 'center'
				},
				render: row => <TacheStatutIcon statut={row.statut} isEnRetard={row.enRetard} />
			})
		}

		result.push({
				key: 'materiel',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.materiel" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '22%',
					align: 'center'
				},
				render: row => {
					const materiels = row.materiels && row.materiels.length > 0 ? row.materiels.join(', ') : '-'
					return <Box style={{ width: '100%' }}>
						<Tooltip title={materiels}>
							<Typography noWrap style={{ textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '12px' }}>
								{materiels}
							</Typography>
						</Tooltip>
					</Box>
				}
			},
			{
				key: 'chargeTravail',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.chargeTravail" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '6%',
					align: 'center'
				},
				render: row => formatDuration(row.chargeTravail)
			},
			{
				key: 'chargeUnitaire',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.chargeParPersonne" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '6%',
					align: 'center'
				},
				render: row => formatDuration(row.chargeUnitaire)
			},
			{
				key: 'nbPersonnes',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.nbPersonnes" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '6%',
					align: 'center'
				},
				render: row => <Typography
					color={row.nbPersonnes > (row.affectations || []).length ? 'error' : 'inherit'}
					style={{ fontSize: '12px' }}
				>
					{row.nbPersonnes}
				</Typography>
			},
			{
				key: 'date',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.date" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '11%'
				},
				render: row => row.dateDebutPlanification ?
					<Box className={classes.lockBoxContainer} style={{ justifyContent: row.lockedDate ? 'space-between' : 'center' }}>
						{row.lockedDate && <LockIcon className={classes.lockIcon} />}
						<Typography style={{ fontSize: '12px', fontWeight: 'bold', textAlign: 'center' }}>
							<FormattedDate
								value={row.dateDebutPlanification}
								weekday="long"
								day="2-digit"
								month="2-digit"
								year="numeric"
								hour="numeric"
								minute="numeric"
							>
								{text => capitalize(text)}
							</FormattedDate>
						</Typography>
					</Box>  : '-'
			},
			{
				key: 'resources',
				name: <FormattedMessage id="gestionTaches.ecranPlanification.table.headers.resources" />,
				sort: false,
				tableHeaderColumnProps: {
					width: '15%',
					align: 'center'
				},
				render: row => {
					const affectations = row.affectations && row.affectations.length > 0 ? row.affectations.join(', ') : '-'
					return <Box className={classes.lockBoxContainer} style={{ justifyContent: row.lockedAffectations ? 'space-between' : 'center' }}>
						{row.lockedAffectations && <LockIcon className={classes.lockIcon} />}
						<Tooltip title={affectations}>
							<Typography noWrap style={{ textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '12px', width: '100%' }}>
								{affectations}
							</Typography>
						</Tooltip>
					</Box>
				}
			})

		return result
	}, [isResultat, classes])

	return (
		<div className={classes.leftContainer}>
			<SpringDataTableWithLoader
				nom={PLANIFICATION_TABLE_NAME}
				mode="post"
				apiUrl={isResultat ? '/solution-taches/planification' : '/taches/planification'}
				preFetch={resetSelectedRows}
				pageSizes={[25, 50, 100]}
				defaultPageSize={100}
				noResultFragment={<NoResultPage />}
				filters={filterValues}
				tableProps={{
					stickyHeader: true,
					className: classes.table
				}}
				headers={headers}
				showBorders={false}
				selectable
				displaySelectAll
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	filters: getFilterValues(state)
})

const actions = {
	resetSelectedRows: () => dataTableActions.selectRows(PLANIFICATION_TABLE_NAME, [])
}

export default compose(
	connect(mapStateToProps, actions),
	openTacheDetail,
	injectTachePanel
)(PlanificationTable)
